// Copyright (c) 2020 WiseTime. All rights reserved.

import { Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap, tap, withLatestFrom } from "rxjs/operators"
import * as Actions from "../actions"
import * as ToolbarSlice from "../slice"
import { RootState } from "app/store"
import { TOOLBAR_FILTERS_KEY } from "./index"
import { PersistedToolbar, TimeRowViewFlag } from "generated/timeline/toolbar/toolbar_pb"

export const serialize = (toolbarState: ToolbarSlice.ToolbarState): string => {
  const toolbar = new PersistedToolbar()
    .setSortType(toolbarState.sortType)
    .setSortOrder(toolbarState.timeBlocksSortOrder)
    .setViewFlagsList(toolbarState.viewFlags.filter(flag => flag !== TimeRowViewFlag.ERRORED))

  // It's not trivial to serialize a Uint8array to base64 string on IE 11,
  // therefore we serialize the binary array as JSON
  const buffer = toolbar.serializeBinary()
  const array = Array.prototype.slice.call(buffer)

  return JSON.stringify(array)
}

export type StorageWriter = (value: string) => void

const persist: StorageWriter = (value) => {
  localStorage.setItem(TOOLBAR_FILTERS_KEY, value)
}

const onToolbarUpdateAction = (action: Action): boolean => ToolbarSlice.setTimeBlocksSortOrder.match(action)
  || ToolbarSlice.setSortType.match(action)
  || ToolbarSlice.addViewFlag.match(action)
  || ToolbarSlice.removeViewFlag.match(action)
  || ToolbarSlice.resetViewFlags.match(action)

export const persistToolbarFilters = (persist: StorageWriter) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(onToolbarUpdateAction),
      withLatestFrom(state$),
      mergeMap(([, state]) => of(state.toolbar).pipe(
        map(serialize),
        tap(persist),
        map(_ => Actions.toolbarFiltersPersisted()),
        catchError(_ => of(Actions.toolbarFiltersPersistFailed())),
      )),
    )

export default persistToolbarFilters(persist)

